'use client';

import { useEffect, useRef, useState } from 'react';

import { formStateAtom } from '@core/Atoms/Pdp/FormState.atom';
import { searchParamsAtom } from '@core/Atoms/SearchParams/SearchParams.atom';
import { userIdAtom } from '@core/Atoms/User/UserId.atom';
import { PAGE_TYPE } from '@core/Constants/PageType';
import { useElementObserver } from '@core/Hooks/UseElementObserver';
import { isHeycarConnectDealer } from '@core/Utils/Vehicle/IsHeycarConnect';
import { Location } from '@gds/Icons/Paths/Location';
import useBreakpoint from '@gds/Pagination/Hooks/UseBreakpoint.hook';
import { Typography } from '@gds/Typography/Typography';
import { experimentsAtom } from '@growthBookExperimentation/Atoms/Experiments.atom';
import { flagsAtom } from '@growthBookExperimentation/Atoms/Flags.atom';
import { useIsExperimentEnabled } from '@growthBookExperimentation/Hooks/useIsExperimetEnabled';
import { useModal } from '@modal/Hooks/UseModal';
import { adminFeeMessagesAtom } from '@shared-components/AdminFee/AdminFee.atoms';
import { ctaClickedToEvent } from '@tracking/Schemas/CtaClicked/CtaClickedToEvent';
import { marketingToEvent } from '@tracking/Schemas/Marketing/MarketingToEvent';
import { userContextToEvent } from '@tracking/Schemas/UserContext/UserContextToEvent';
import { trackCustomEvent } from '@tracking/Utils/TrackCustomEvent';
import { useAtomValue, useSetAtom } from 'jotai';

import { dealerNumberErrorAtom } from 'Atoms/App/Pdp/DealerNumberError.atom';
import { showDealerNumberPdpAtom } from 'Atoms/App/Pdp/ShowDealerNumberPdp.atom';
import { VehicleAttributes } from 'Components/VehicleAttributes/VehicleAttributes';

import { GuaranteeWrapper } from '../GuaranteeWrapper/GuaranteeWrapper';
import { LeadCTAs } from '../LeadCTAs/LeadCTAs';
import { SavedSearchCta } from '../SavedSearchCta/SavedSearchCta';

import { StickyInfoProps } from './StickyInfo.entity';

import styles from './StickyInfo.module.css';

export const StickyInfo = ({
  locale,
  strings: {
    pdpStrings,
    vehicleAttributesStrings,
    leadFormMessages,
    reserveNowFaqContent,
    autobizStrings,
    guaranteeStrings,
    priceRatingStrings,
    distanceToDealerString,
    savedSearchMessages,
    adminFeeStrings,
  },
  vehicle,
  hasSmallPrint,
  hasCampaignDiscount,
  showUspPills,
  usps,
  autobizUrl,
  isStockUnavailable,
}: StickyInfoProps) => {
  const $stickyInfo = useRef<HTMLDivElement>(null);
  const hasStartedAutoGenerateNo = useRef(false);
  const flags = useAtomValue(flagsAtom);
  const [isSticky, setIsSticky] = useState(false);
  const {
    breakpoints: { isMobile },
  } = useBreakpoint();
  const experiments = useAtomValue(experimentsAtom);

  const setIsNumberVisible = useSetAtom(showDealerNumberPdpAtom);
  const setDealerNumberError = useSetAtom(dealerNumberErrorAtom);
  const setAdminFeeStrings = useSetAtom(adminFeeMessagesAtom);

  const isVehicleReservedOrInactive =
    vehicle.details.status === 'reserved' || vehicle.details.status === 'inactive';
  useEffect(() => {
    setIsNumberVisible(false);
    setDealerNumberError(false);
    setAdminFeeStrings(adminFeeStrings);
  }, []);

  // Observer for sticky info height
  useElementObserver($stickyInfo, entry => {
    const contentBoxSize = entry.contentBoxSize[0];
    const height = contentBoxSize.blockSize;
    const root = document.querySelector(':root') as HTMLElement;

    if (window.scrollY > 80 && window.innerWidth > 1023) {
      root.style.setProperty('--sticky-info-height', `${height}px`);
    } else {
      root.style.removeProperty('--sticky-info-height');
    }
  });

  // Scroll event for UspsItems condition
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 80 && window.innerWidth > 1023);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const { openModal } = useModal();
  const userId = useAtomValue(userIdAtom);
  const searchParams = useAtomValue(searchParamsAtom);
  const formState = useAtomValue(formStateAtom);

  const handleOpenModal = () => {
    const ctaEventObj = {
      ctaType: 'button',
      ctaWording: 'Estimer ma voiture (CTAs)',
      targetUrl: 'none',
    };
    const userData = {
      userId,
      values: { ...formState },
    };
    const marketingDataObj = {
      marketingValues: { ...(searchParams ?? {}) },
    };
    const userEvent = userContextToEvent(userData);
    const marketingEvent = marketingToEvent(marketingDataObj);

    const context = [userEvent, marketingEvent];

    trackCustomEvent({
      event: ctaClickedToEvent(ctaEventObj),
      context,
    });

    openModal(
      <iframe
        src={`${autobizUrl}/site/page-accueil?layout=iframe&vehicle_uid=${vehicle.heycarId}&dealer_uid=${vehicle.dealer.id}`}
        title={autobizStrings.header}
        width="100%"
      ></iframe>,
      {
        header: autobizStrings.header,
        modalClassName: styles.modalContainer,
        modalBodyClassName: styles.modalBody,
      },
    );
  };

  const isPrimaryRedesignExpEnabled = useIsExperimentEnabled('navigation-redesign-experiment', [
    '1',
    '2',
  ]);
  const hasHeyCarConnect = flags.heycarConnect && isHeycarConnectDealer(vehicle);

  const shouldShowAutobiz =
    locale === 'fr' && flags.autobizPdpViewportTest && vehicle?.details.status === 'active';
  const shouldShowLocation =
    isMobile &&
    distanceToDealerString &&
    experiments.some(
      exp => exp.experimentId === 'location-pdp-experiment' && exp.variationId === '1',
    );

  return (
    <>
      {['stickyInfo', 'stickyInfoCompact'].map(infoType => (
        <div
          ref={$stickyInfo}
          className={`${styles[infoType]} ${isSticky ? styles.sticky : ''}`}
          id="sticky-info"
          {...(infoType === 'stickyInfoCompact' && {
            'data-is-primary-nav-enabled': isPrimaryRedesignExpEnabled,
          })}
          data-test-id={infoType}
          key={infoType}
        >
          <VehicleAttributes
            strings={vehicleAttributesStrings}
            locale={locale}
            vehicle={vehicle}
            hasSmallPrint={hasSmallPrint}
            pageType={PAGE_TYPE.Pdp}
            index={null}
            hasCampaignDiscount={hasCampaignDiscount}
            usps={usps}
            showUspPills={showUspPills}
            priceRatingStrings={priceRatingStrings}
            infoType={infoType}
            isStockUnavailable={isStockUnavailable}
          />
          {vehicle.spec?.guarantee && (
            <GuaranteeWrapper messages={guaranteeStrings} guarantee={vehicle.spec.guarantee} />
          )}

          {shouldShowLocation && infoType === 'stickyInfo' && !hasHeyCarConnect && (
            <Typography
              data-variant="has-distance"
              variant="caption1"
              Component="span"
              className={styles.location}
            >
              <Location fontSize={20} />
              {distanceToDealerString}
            </Typography>
          )}
          {!isStockUnavailable ? (
            <LeadCTAs
              vehicle={vehicle}
              strings={{ pdpStrings, leadFormMessages, reserveNowFaqContent }}
              isEcom={!!vehicle.ecom.isEcom}
              locale={locale}
              hasReservedOrInactiveStatus={
                isVehicleReservedOrInactive && flags.vehicleWithReservedStatus
              }
              isSticky={isSticky}
              hasStartedAutoGenerateNo={hasStartedAutoGenerateNo}
            />
          ) : (
            <>
              <Typography className={styles.stockUnavailable} variant={'subheading4'}>
                {pdpStrings.unavailableVehicleLabel}
              </Typography>
              <SavedSearchCta
                strings={savedSearchMessages}
                make={vehicle.make.id}
                model={vehicle.model.id}
                locale={locale}
              />
            </>
          )}
          {shouldShowAutobiz && !hasHeyCarConnect && (
            <Typography
              className={styles.autobizCentralButton}
              variant={'subheading3'}
              onClick={handleOpenModal}
            >
              {autobizStrings.autobizCta}
            </Typography>
          )}
        </div>
      ))}
    </>
  );
};

StickyInfo.displayName = 'StickyInfo';
